import React from 'react';
import {
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
} from 'recharts';
import { Clock } from 'lucide-react';

interface DualHourlyChartProps {
  actual: Record<string, number>;
  anterior: Record<string, number>;
}

/**
 * Componente que muestra una comparativa de actividad por hora entre dos periodos
 * con un diseño mejorado y gradientes
 */
export function DualHourlyChart({ actual, anterior }: DualHourlyChartProps) {
  // Preparar datos para el gráfico
  const chartData = Array(24).fill(0).map((_, i) => {
    const hour = i.toString();
    return {
      hora: `${String(i).padStart(2, '0')}:00`,
      actual: actual[hour] || 0,
      anterior: anterior[hour] || 0
    };
  });

  // Personalización del tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[color:var(--acc2)]/20 backdrop-blur-sm p-3 rounded-lg border border-[color:var(--acc1)]/25 shadow-lg">
          <p className="text-white font-medium">{label}</p>
          <div className="space-y-1 mt-1">
            {payload.map((entry: any, index: number) => (
              <div 
                key={index} 
                className="flex justify-between gap-4 text-sm"
              >
                <span style={{ color: index === 1 ? '#A78BFA' : '#93C5FD' }}>
                  {entry.name}:
                </span>
                <span className="font-medium text-white">
                  {entry.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-[linear-gradient(135deg,var(--acc1),var(--acc2))] backdrop-blur-sm border border-[color:var(--acc1)]/20 rounded-xl p-6 shadow-lg transition-all duration-300 hover:shadow-xl">
      <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
        <Clock className="mr-2 text-[color:var(--acc1)]/90" size={20} />
        Actividad por Hora
      </h2>

      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#A855F7" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#6D28D9" stopOpacity={0.2} />
              </linearGradient>
              <linearGradient id="colorAnterior" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#1E40AF" stopOpacity={0.2} />
              </linearGradient>
            </defs>
            
            <CartesianGrid strokeDasharray="3 3" stroke="#4f46e5" opacity={0.15} vertical={false} />
            
            <XAxis 
              dataKey="hora" 
              stroke="#c7d2fe" 
              fontSize={12}
              tickLine={false}
              axisLine={{ stroke: '#4f46e530' }}
              interval={Math.floor(chartData.length / 6)} 
            />
            
            <YAxis 
              stroke="#c7d2fe" 
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            
            <Tooltip 
              content={<CustomTooltip />}
              cursor={{ stroke: '#4338ca', strokeWidth: 1, strokeDasharray: '4 4' }}
            />
            
            <Area 
              type="monotone" 
              dataKey="anterior" 
              name="Semana Anterior"
              stroke="#3B82F6" 
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorAnterior)" 
            />
            
            <Area 
              type="monotone" 
              dataKey="actual" 
              name="Semana Actual"
              stroke="#A855F7" 
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorActual)" 
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      {/* Leyenda simple y limpia */}
      <div className="flex justify-center mt-4 space-x-6 text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-600 rounded-sm mr-2"></div>
          <span className="text-[color:var(--acc1)]/85">Semana Anterior</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-[color:var(--acc2)] rounded-sm mr-2"></div>
          <span className="text-[color:var(--acc1)]/85">Semana Actual</span>
        </div>
      </div>
    </div>
  );
}

export default DualHourlyChart;